// QuestionResponder — Componente "universal" para responder cualquier tipo de
// pregunta del banco. Se usa en el modal del estudiante durante una pregunta
// interactiva de clase en vivo. Soporta: multiple_choice, numeric,
// fill_in_blank, open_text, likert, scale_1_10, table_input.
import { useState } from "react";
import { RenderTextWithMath } from "@/components/MathFormulaEditor";

export default function QuestionResponder({ question, onSubmit, disabled = false }) {
  const [value, setValue] = useState(null);
  const type = question?.type;

  if (!question) return null;

  const renderInput = () => {
    switch (type) {
      case "multiple_choice":
        return (
          <div className="space-y-2" data-testid="resp-mc">
            {(question.options || []).map((o, i) => {
              const k = o.value ?? o.label;
              const sel = value === k;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => setValue(k)}
                  data-testid={`resp-mc-opt-${i}`}
                  className={`w-full text-left px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${sel ? "border-[#A43790] bg-[#FFF1F8]" : "border-[#E1E5F0] bg-white hover:border-[#A43790]"}`}
                >
                  <span className="inline-block w-6 h-6 rounded-full bg-[#1F2547] text-white text-xs font-extrabold text-center leading-6 mr-2">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <RenderTextWithMath text={o.label || ""} />
                </button>
              );
            })}
          </div>
        );
      case "numeric":
      case "fill_in_blank":
        return (
          <input
            type={type === "numeric" ? "number" : "text"}
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => setValue(e.target.value)}
            placeholder={type === "numeric" ? "Ingresa un número" : "Tu respuesta"}
            data-testid="resp-numeric-input"
            className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm font-medium outline-none focus:border-[#A43790]"
          />
        );
      case "open_text":
      case "open":
        return (
          <textarea
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            placeholder="Escribe tu respuesta…"
            data-testid="resp-open-input"
            className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm font-medium outline-none focus:border-[#A43790]"
          />
        );
      case "likert": {
        const labels = question.scale_labels || ["Muy en desacuerdo", "En desacuerdo", "Neutral", "De acuerdo", "Muy de acuerdo"];
        return (
          <div className="flex gap-1.5 flex-wrap" data-testid="resp-likert">
            {labels.map((lbl, i) => {
              const v = i + 1;
              const sel = value === v;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => setValue(v)}
                  data-testid={`resp-likert-${v}`}
                  className={`flex-1 min-w-[100px] px-2 py-2 rounded-xl border-2 text-xs font-bold transition-colors ${sel ? "border-[#A43790] bg-[#FFF1F8]" : "border-[#E1E5F0] bg-white hover:border-[#A43790]"}`}
                >
                  <div className="text-base mb-0.5">{v}</div>
                  {lbl}
                </button>
              );
            })}
          </div>
        );
      }
      case "scale_1_10":
        return (
          <div data-testid="resp-scale">
            <div className="flex justify-between text-[10px] text-[#6E7591] mb-1">
              <span>1 · Mínimo</span><span>10 · Máximo</span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={value ?? 5}
              disabled={disabled}
              onChange={(e) => setValue(parseInt(e.target.value, 10))}
              data-testid="resp-scale-input"
              className="w-full accent-[#A43790]"
            />
            <div className="text-center font-display text-3xl font-extrabold text-[#A43790]">{value ?? 5}</div>
          </div>
        );
      case "table_input": {
        const cfg = question.table_config || { rows: 2, cols: 2, headers: [] };
        const rows = cfg.rows || 2;
        const cols = cfg.cols || 2;
        const grid = Array.isArray(value) ? value : Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
        return (
          <div className="overflow-x-auto" data-testid="resp-table">
            <table className="border-collapse">
              <tbody>
                {grid.map((r, ri) => (
                  <tr key={ri}>
                    {r.map((c, ci) => (
                      <td key={ci} className="border border-[#E1E5F0]">
                        <input
                          value={c}
                          disabled={disabled}
                          onChange={(e) => {
                            const next = grid.map((row) => row.slice());
                            next[ri][ci] = e.target.value;
                            setValue(next);
                          }}
                          data-testid={`resp-table-cell-${ri}-${ci}`}
                          className="w-16 px-1 py-1 text-xs text-center outline-none"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      default:
        return (
          <p className="text-xs text-[#6E7591] italic">
            Tipo de pregunta no soportado para respuesta en vivo ({type}). Pide al docente revisarlo.
          </p>
        );
    }
  };

  const canSubmit = value !== null && value !== "" && !disabled;

  return (
    <div className="space-y-3" data-testid="question-responder">
      <div>
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] mb-1">
          Pregunta · {type}
        </p>
        <p className="text-base font-bold text-[#1F2547] leading-snug" data-testid="resp-question-text">
          <RenderTextWithMath text={question.text || ""} />
        </p>
        {question.image_url && (
          <img src={question.image_url} alt="" className="mt-2 max-h-48 rounded-lg border" />
        )}
      </div>
      {renderInput()}
      {onSubmit && (
        <button
          type="button"
          onClick={() => onSubmit(value)}
          disabled={!canSubmit}
          data-testid="resp-submit"
          className="w-full jasq-btn-primary disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          Enviar respuesta
        </button>
      )}
    </div>
  );
}
